import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

// Types for the new budget system
type BudgetPeriodType = 'H1' | 'H2' | 'ANNUAL';
type BudgetPeriodStatus = 'draft' | 'active' | 'locked' | 'archived';

interface BudgetPeriod {
  id: number;
  period_type: BudgetPeriodType;
  year: number;
  start_date: string;
  end_date: string;
  status: BudgetPeriodStatus;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface CreateBudgetPeriodData {
  period_type: BudgetPeriodType;
  year: number;
  notes?: string;
}

interface CopyBudgetPeriodData {
  target_period_type: BudgetPeriodType;
  target_year: number;
  notes?: string;
}

const BudgetPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [periods, setPeriods] = useState<BudgetPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState<CreateBudgetPeriodData>({
    period_type: 'H1',
    year: new Date().getFullYear(),
    notes: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourcePeriod, setCopySourcePeriod] = useState<BudgetPeriod | null>(null);
  const [copyData, setCopyData] = useState<CopyBudgetPeriodData>({
    target_period_type: 'H1',
    target_year: new Date().getFullYear(),
    notes: '',
  });
  const [copying, setCopying] = useState(false);

  // Guards against out-of-order responses clobbering fresher state: fetchPeriods
  // can be (and, under React.StrictMode in development, reliably is) in flight
  // more than once at a time - e.g. the mount effect below fires twice, and a
  // create/delete/status-update action triggers another call to refresh the
  // list while an earlier call is still pending. Without this guard, whichever
  // response happens to arrive *last* wins, even if it was dispatched first and
  // is now stale - which could silently revert the list to a pre-creation state
  // right after a successful create. Only the response belonging to the most
  // recently dispatched request is ever applied to state.
  const latestFetchIdRef = useRef(0);

  // `fetchPeriods` is also used to silently refresh the list in the background
  // (after create/copy/delete/status-change actions), while the page may still
  // have a modal open. `loading` gates a full-page early return below that
  // replaces *everything* - including any open modal - with a spinner, so it
  // must only be toggled for the real initial page load; flipping it on every
  // background refresh would tear the open modal (and the rest of the page)
  // out of the DOM for the duration of that refresh, before the fresh data
  // even arrives. Background refreshes instead update `periods` in place once
  // the response lands, with no visible full-page interruption.
  const fetchPeriods = useCallback(async (options?: { silent?: boolean }) => {
    const fetchId = ++latestFetchIdRef.current;
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      setError(null);
      const response = await fetch(apiUrl('budgets/periods'), {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (fetchId === latestFetchIdRef.current) {
          setPeriods(data);
        }
      } else {
        throw new Error('Failed to fetch budget periods');
      }
    } catch (error) {
      console.error('Failed to fetch budget periods:', error);
      if (fetchId === latestFetchIdRef.current) {
        setError('Failed to load budget periods');
      }
    } finally {
      if (fetchId === latestFetchIdRef.current && !options?.silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  const handleCreatePeriod = async () => {
    if (!createData.period_type || !createData.year) return;

    try {
      setCreating(true);
      setCreateError(null); // Clear any previous errors

      const response = await fetch(apiUrl('budgets/periods'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(createData),
      });

      if (response.ok) {
        // Refresh the list *before* closing the modal/clearing the form, so
        // that by the time callers (and tests) observe the modal having
        // closed, the newly created period is already reflected in state.
        await fetchPeriods({ silent: true });
        setShowCreateModal(false);
        setCreateData({
          period_type: 'H1',
          year: new Date().getFullYear(),
          notes: '',
        });
        setCreateError(null);
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        setCreateError(errorMessage);
        console.error('Failed to create budget period:', errorMessage);
        // Don't close modal on error, let user see the error and retry
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      setCreateError(errorMessage);
      console.error('Failed to create budget period:', error);
      // Don't close modal on error, let user see the error and retry
    } finally {
      setCreating(false);
    }
  };

  const handleCopyPeriod = async () => {
    if (!copySourcePeriod || !copyData.target_period_type || !copyData.target_year) return;

    try {
      setCopying(true);
      const response = await fetch(apiUrl(`budgets/periods/${copySourcePeriod.id}/copy`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(copyData),
      });

      if (response.ok) {
        setShowCopyModal(false);
        setCopySourcePeriod(null);
        setCopyData({
          target_period_type: 'H1',
          target_year: new Date().getFullYear(),
          notes: '',
        });
        await fetchPeriods({ silent: true });
        alert('Budget period copied successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to copy budget period: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to copy budget period:', error);
      alert('Failed to copy budget period');
    } finally {
      setCopying(false);
    }
  };

  const openCopyModal = (period: BudgetPeriod) => {
    setCopySourcePeriod(period);
    setCopyData({
      target_period_type: period.period_type, // Default to same type
      target_year: period.year + 1, // Default to next year
      notes: `Copied from ${formatPeriodType(period.period_type)} ${period.year}`,
    });
    setShowCopyModal(true);
  };

  const formatPeriodType = (type: BudgetPeriodType) => {
    switch (type) {
      case 'H1':
        return 'H1 (Jan-Jun)';
      case 'H2':
        return 'H2 (Jul-Dec)';
      case 'ANNUAL':
        return 'Annual';
      default:
        return type;
    }
  };

  const getStatusColor = (status: BudgetPeriodStatus) => {
    switch (status) {
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'locked':
        return 'bg-blue-100 text-blue-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const updatePeriodStatus = async (periodId: number, newStatus: BudgetPeriodStatus) => {
    try {
      setUpdatingStatus(periodId);
      setError(null);

      const response = await fetch(apiUrl(`budgets/periods/${periodId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${newStatus} budget period`);
      }

      // Refresh the periods list
      await fetchPeriods({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const canUnlockBudget = () => {
    return user?.role === 'superadmin' || user?.role === 'admin';
  };

  const canDeleteBudget = (_period: BudgetPeriod) => {
    if (user?.role === 'superadmin') {
      return true; // Superadmin can delete any budget
    }
    if (user?.role === 'admin') {
      return true; // Admin can delete any budget (including locked)
    }
    return false; // Other roles cannot delete
  };

  const deleteBudgetPeriod = async (periodId: number) => {
    if (
      !confirm('Are you sure you want to delete this budget period? This action cannot be undone.')
    ) {
      return;
    }

    try {
      setError(null);
      const response = await fetch(apiUrl(`budgets/periods/${periodId}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete budget period');
      }

      // Optimistically remove from UI immediately
      setPeriods((prev) => prev.filter((p) => p.id !== periodId));
      // Also refresh from server for consistency
      await fetchPeriods({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete budget period');
    }
  };

  const BudgetPeriodCard: React.FC<{ period: BudgetPeriod }> = ({ period }) => (
    <div
      className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
      data-testid={`budget-period-card-${period.id}`}
    >
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {formatPeriodType(period.period_type)} {period.year}
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              {new Date(period.start_date).toLocaleDateString('fi-FI')} -{' '}
              {new Date(period.end_date).toLocaleDateString('fi-FI')}
            </p>
          </div>
          <span
            className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
              period.status
            )}`}
          >
            {period.status.toUpperCase()}
          </span>
        </div>

        {period.notes && <p className="text-gray-600 text-sm mb-4">{period.notes}</p>}

        <div className="flex flex-col sm:flex-row gap-2">
          {/* Main manage button - disabled if locked */}
          <button
            onClick={() => navigate(`/budget/periods/${period.id}/costpoints`)}
            className={`flex-1 px-3 py-2 rounded-md transition-colors text-sm font-medium ${
              period.status === 'locked'
                ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            data-testid={`manage-budget-button-${period.id}`}
          >
            {period.status === 'locked' ? 'View' : 'Manage'}
          </button>

          {/* Action buttons row */}
          <div className="flex gap-2">
            {/* Copy button */}
            <button
              onClick={() => openCopyModal(period)}
              className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              title="Copy this budget period"
            >
              📋 Copy
            </button>

            {/* Lock/Unlock button */}
            {period.status === 'locked' ? (
              canUnlockBudget() ? (
                <button
                  onClick={() => updatePeriodStatus(period.id, 'active')}
                  disabled={updatingStatus === period.id}
                  className="bg-yellow-600 text-white px-3 py-2 rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {updatingStatus === period.id ? '...' : '🔓'}
                </button>
              ) : (
                <div className="bg-gray-300 text-gray-500 px-3 py-2 rounded-md text-sm font-medium cursor-not-allowed">
                  🔒
                </div>
              )
            ) : (
              <button
                onClick={() => updatePeriodStatus(period.id, 'locked')}
                disabled={updatingStatus === period.id}
                className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {updatingStatus === period.id ? '...' : '🔒'}
              </button>
            )}

            {/* Delete button */}
            {canDeleteBudget(period) && (
              <button
                onClick={() => deleteBudgetPeriod(period.id)}
                className="bg-red-700 text-white px-3 py-2 rounded-md hover:bg-red-800 transition-colors text-sm font-medium"
                title="Delete budget period"
                data-testid={`delete-budget-button-${period.id}`}
              >
                🗑️
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading budget periods...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Budget Management</h1>
            <p className="text-gray-600 mt-1">Manage budget periods and costpoint allocations</p>
          </div>
          <button
            onClick={() => {
              setCreateError(null); // Clear any previous errors
              setShowCreateModal(true);
            }}
            className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-medium"
            data-testid="create-budget-period-button"
          >
            Create New Budget Period
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => fetchPeriods()}
                    className="bg-red-100 text-red-800 px-3 py-1 rounded-md text-sm hover:bg-red-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Budget Periods */}
        {periods.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {periods.map((period) => (
              <BudgetPeriodCard key={period.id} period={period} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg
                className="mx-auto h-16 w-16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Budget Periods</h3>
            <p className="text-gray-500 mb-4">
              Create your first budget period to start managing costpoint budgets.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Create Budget Period
            </button>
          </div>
        )}
      </div>

      {/* Create Budget Period Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          data-testid="create-budget-period-modal"
        >
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Budget Period</h3>

              {/* Error Display */}
              {createError && (
                <div
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
                  data-testid="create-budget-error"
                >
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{createError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Period Type
                  </label>
                  <select
                    value={createData.period_type}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        period_type: e.target.value as BudgetPeriodType,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    data-testid="period-type-select"
                  >
                    <option value="H1">H1 (Jan-Jun)</option>
                    <option value="H2">H2 (Jul-Dec)</option>
                    <option value="ANNUAL">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="number"
                    value={createData.year}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        year: parseInt(e.target.value, 10) || new Date().getFullYear(),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    min="2020"
                    max="2030"
                    data-testid="year-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={createData.notes}
                    onChange={(e) =>
                      setCreateData({
                        ...createData,
                        notes: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Add notes about this budget period..."
                    data-testid="notes-textarea"
                  />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleCreatePeriod}
                  disabled={creating}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  data-testid="create-period-submit-button"
                >
                  {creating ? 'Creating...' : 'Create Period'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateData({
                      period_type: 'H1',
                      year: new Date().getFullYear(),
                      notes: '',
                    });
                  }}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Budget Period Modal */}
      {showCopyModal && copySourcePeriod && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Copy Budget Period</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Source:</strong> {formatPeriodType(copySourcePeriod.period_type)}{' '}
                  {copySourcePeriod.year}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  All budget items, tags, and costpoint statuses will be copied to the new period.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Period Type
                  </label>
                  <select
                    value={copyData.target_period_type}
                    onChange={(e) =>
                      setCopyData({
                        ...copyData,
                        target_period_type: e.target.value as BudgetPeriodType,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="H1">H1 (Jan-Jun)</option>
                    <option value="H2">H2 (Jul-Dec)</option>
                    <option value="ANNUAL">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Year
                  </label>
                  <input
                    type="number"
                    value={copyData.target_year}
                    onChange={(e) =>
                      setCopyData({
                        ...copyData,
                        target_year: parseInt(e.target.value, 10) || new Date().getFullYear(),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    min="2020"
                    max="2030"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={copyData.notes}
                    onChange={(e) =>
                      setCopyData({
                        ...copyData,
                        notes: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    rows={3}
                    placeholder="Add notes about this copied budget period..."
                  />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleCopyPeriod}
                  disabled={copying}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {copying ? 'Copying...' : '📋 Copy Budget Period'}
                </button>
                <button
                  onClick={() => {
                    setShowCopyModal(false);
                    setCopySourcePeriod(null);
                    setCopyData({
                      target_period_type: 'H1',
                      target_year: new Date().getFullYear(),
                      notes: '',
                    });
                  }}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetPage;
